import { Directive, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';

export const DEFAULT_LONG_PRESS_DURATION_MS = 500;
const MOVE_CANCEL_THRESHOLD_PX = 10;

/**
 * Emits {@link longPress} when the user presses and holds (touch/pen) on the
 * host element for {@link longPressDurationMs} without moving.
 */
@Directive({
  selector: '[itlLongPress]',
  standalone: false,
})
export class LongPressDirective implements OnDestroy {
  @Input() public longPressDurationMs = DEFAULT_LONG_PRESS_DURATION_MS;
  @Input() public longPressDisabled = false;

  @Output() public longPress = new EventEmitter<void>();

  private _timeoutId: ReturnType<typeof setTimeout> | null = null;
  private _startX = 0;
  private _startY = 0;

  @HostListener('pointerdown', ['$event'])
  public onPointerDown(event: PointerEvent): void {
    if (this.longPressDisabled || event.pointerType === 'mouse') {
      return;
    }
    this._startX = event.clientX;
    this._startY = event.clientY;
    this.clearTimer();
    this._timeoutId = setTimeout(() => {
      this._timeoutId = null;
      this.longPress.emit();
    }, this.longPressDurationMs);
  }

  @HostListener('pointermove', ['$event'])
  public onPointerMove(event: PointerEvent): void {
    if (this._timeoutId === null) {
      return;
    }
    const deltaX = Math.abs(event.clientX - this._startX);
    const deltaY = Math.abs(event.clientY - this._startY);
    if (deltaX > MOVE_CANCEL_THRESHOLD_PX || deltaY > MOVE_CANCEL_THRESHOLD_PX) {
      this.clearTimer();
    }
  }

  @HostListener('pointerup')
  @HostListener('pointercancel')
  @HostListener('pointerleave')
  public onPointerEnd(): void {
    this.clearTimer();
  }

  public ngOnDestroy(): void {
    this.clearTimer();
  }

  private clearTimer(): void {
    if (this._timeoutId !== null) {
      clearTimeout(this._timeoutId);
      this._timeoutId = null;
    }
  }
}
